import React, { useCallback, useEffect, useState } from "react";

// Public, unauthenticated proposal page reached from the private link a
// prospect is emailed when staff "Send" a proposal: /proposal/{token}.
// Accepting starts a real, tracked Stripe Checkout Session (the backend
// returns its URL) - never a static payment link.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const money = (value) =>
  typeof value === "number" ? `$${value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}` : "—";

async function call(path, method = "GET") {
  const res = await fetch(`${BACKEND_URL}/api/public/proposals/${path}`, { method, headers: { "Content-Type": "application/json" } });
  let data = null;
  try {
    data = await res.json();
  } catch {
    // no JSON body
  }
  if (!res.ok) {
    const err = new Error((data && typeof data.detail === "string" && data.detail) || "Something went wrong. Please try again.");
    err.status = res.status;
    throw err;
  }
  return data;
}

function Section({ title, items }) {
  if (!Array.isArray(items) || !items.length) return null;
  return (
    <div className="next-steps-section" style={{ textAlign: "left" }}>
      <h3>{title}</h3>
      <ul style={{ margin: 0, paddingLeft: 20, lineHeight: 1.7, color: "#d0c9bd" }}>
        {items.map((item, i) => <li key={i}>{String(item)}</li>)}
      </ul>
    </div>
  );
}

export default function PublicProposalPage({ token }) {
  const [proposal, setProposal] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const load = useCallback(async () => {
    try {
      setProposal(await call(encodeURIComponent(token)));
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const startCheckout = async (action) => {
    setBusy(true);
    setNotice(null);
    try {
      const res = await call(`${encodeURIComponent(token)}/${action}`, "POST");
      if (res.checkout_url) {
        window.location.href = res.checkout_url;
        return;
      }
      setNotice(res.payment_message || "Online payment isn't available right now. Your advisor will follow up.");
      await load();
    } catch (err) {
      setNotice(err.message);
    }
    setBusy(false);
  };

  if (error) {
    return (
      <div className="public-payment-page" data-testid="public-proposal-page">
        <div className="payment-card glass-panel">
          <h1>Proposal unavailable</h1>
          <p className="error-message" data-testid="public-proposal-error">{error}</p>
          <p className="support-text">Questions? <a href="mailto:support@medspabookings.co">support@medspabookings.co</a></p>
        </div>
      </div>
    );
  }

  if (!proposal) {
    return (
      <div className="public-payment-page" data-testid="public-proposal-page">
        <div className="payment-card glass-panel"><div className="loading-spinner"><div className="spinner" /><p>Loading your proposal...</p></div></div>
      </div>
    );
  }

  const status = proposal.status;
  return (
    <div className="public-payment-page" data-testid="public-proposal-page">
      <div className="payment-card glass-panel" style={{ maxWidth: 820 }}>
        <p className="eyebrow">{proposal.offer_name}</p>
        <h1>Proposal for {proposal.clinic_name}</h1>
        {proposal.executive_summary && <p style={{ textAlign: "left", lineHeight: 1.7, whiteSpace: "pre-line" }}>{proposal.executive_summary}</p>}

        <Section title="What we found" items={proposal.problems_found} />
        <Section title="Growth opportunities" items={proposal.growth_opportunities} />
        <Section title="Implementation plan" items={proposal.implementation_plan} />
        <Section title="Expected results" items={proposal.expected_results} />
        <Section title="Next steps" items={proposal.next_steps} />

        <div className="payment-details">
          <div className="detail-row"><span className="detail-label">Setup fee (one-time)</span><span className="detail-value">{money(proposal.setup_fee)}</span></div>
          <div className="detail-row"><span className="detail-label">Monthly management</span><span className="detail-value">{money(proposal.monthly_management)}/month</span></div>
          {proposal.ad_spend && <div className="detail-row"><span className="detail-label">Ad spend</span><span className="detail-value">{String(proposal.ad_spend)}</span></div>}
          <div className="detail-row"><span className="detail-label">Status</span><span className="detail-value" data-testid="public-proposal-status">{status}</span></div>
        </div>

        {notice && <p className="error-message" data-testid="public-proposal-notice">{notice}</p>}

        <div className="action-buttons">
          {proposal.can_accept && !confirming && (
            <button className="primary-btn" data-testid="public-proposal-accept-button" disabled={busy} onClick={() => setConfirming(true)}>
              {status === "Accepted" ? "Continue to payment" : "Accept proposal"}
            </button>
          )}
          {proposal.can_accept && confirming && (
            <div className="next-steps-section" style={{ textAlign: "left" }}>
              <p>By accepting, {proposal.clinic_name} agrees to proceed with the {proposal.offer_name}. You&apos;ll be taken to Stripe to pay the {money(proposal.setup_fee)} setup fee securely.</p>
              <div className="button-row" style={{ marginTop: 16, display: "flex", gap: 12 }}>
                <button className="ghost-btn" disabled={busy} onClick={() => setConfirming(false)}>Cancel</button>
                <button className="primary-btn" data-testid="public-proposal-confirm-accept" disabled={busy} onClick={() => startCheckout("accept")}>
                  {busy ? "Preparing secure checkout..." : "Accept & continue to payment"}
                </button>
              </div>
            </div>
          )}
          {status === "Paid" && (
            <p className="support-text">Setup fee received - thank you! Check your email for the link to activate your client portal.</p>
          )}
          {proposal.can_start_subscription && (
            <button className="primary-btn" data-testid="public-proposal-subscribe-button" disabled={busy} onClick={() => startCheckout("subscribe")}>
              {busy ? "Preparing secure checkout..." : `Start monthly management (${money(proposal.monthly_management)}/month)`}
            </button>
          )}
          {proposal.subscription_active && <p className="support-text">Monthly management is active.</p>}
          <a className="ghost-btn" href={`${BACKEND_URL}/api/public/proposals/${encodeURIComponent(token)}/pdf`} data-testid="public-proposal-pdf-link">Download PDF</a>
        </div>

        {proposal.link_expires_at && <p className="support-text">This private link expires {new Date(proposal.link_expires_at).toLocaleDateString()}.</p>}
        <p className="support-text">Questions? <a href="mailto:support@medspabookings.co">support@medspabookings.co</a></p>
      </div>
    </div>
  );
}
